import React, {useMemo} from 'react';
import {ArrowDownTrayIcon} from '@heroicons/react/24/outline';
import {useReactTable, getCoreRowModel, createColumnHelper} from '@tanstack/react-table';
import {useGetInvoices, type InvoiceType} from 'api/generated';
import {format} from 'date-fns';
import {TableCommon} from 'components';

const columnHelper = createColumnHelper<InvoiceType>();

interface IBillingTableInvoiceProps {}

const BillingTableInvoice = ({}: IBillingTableInvoiceProps) => {
  const {data: invoicesData} = useGetInvoices({query: {staleTime: 5 * 1000}});
  const invoices = useMemo(() => invoicesData?.data?.data ?? [], [invoicesData?.data?.data]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('createdAt', {
        header: () => <span>Date</span>,
        cell: info => <span>{format(new Date(info.getValue()!), 'd MMM y')}</span>,
      }),
      columnHelper.accessor('metadata.subscription.name', {
        header: () => <span>Plan</span>,
        cell: info => <span>{info.getValue()}</span>,
      }),
      columnHelper.accessor('metadata.subscription', {
        header: () => <span>Billing Period</span>,
        cell: info => {
          if (info.getValue()?.start) {
            const start = info.getValue()?.start!;
            const end = info.getValue()?.end!;
            const formatStart = format(new Date(start), 'dd MMM y');
            const formatEnd = format(new Date(end), 'dd MMM y');
            return <span>{`${formatStart} - ${formatEnd}`}</span>;
          }
          return null;
        },
      }),
      columnHelper.accessor('priceCents', {
        header: () => <span>Amount</span>,
        cell: info => <span className="uppercase">{`${(info.getValue() / 100).toFixed(2)} ${info.row.original.currency}`}</span>,
      }),
      columnHelper.accessor('state', {
        header: () => <span>Status</span>,
        cell: info => <span>{info.getValue().toUpperCase()}</span>,
      }),
      columnHelper.accessor('invoiceUrl', {
        header: () => <span>Invoice</span>,
        cell: info => (
          <div className="flex items-center gap-3">
            <a href={info.getValue()} target="_blank" rel="noreferrer noopener" className="text-primary hover:brightness-120">
              {info.row.original.code}
            </a>
            <a href={info.row.original.invoicePdf} target="_blank" rel="noreferrer noopener">
              <ArrowDownTrayIcon className="size-5 text-primary hover:brightness-120 cursor-pointer" />
            </a>
          </div>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: invoices,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return <TableCommon table={table} />;
};

export default React.memo(BillingTableInvoice);
